import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Send,
  Loader2,
  CheckCircle2,
  Rocket,
  Star,
  SlidersHorizontal,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  BarChart3,
  PieChart as PieChartIcon,
  ChevronRight,
  Mic,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import useStore from '@/store/useStore';
import { useUiLanguage } from '@/lib/i18n';
import { apiMerchantAiBriefing, apiMerchantDecisionInsights, apiSendChat } from '@/services/api';
import { AI_ACTION_TYPES, executeAIAction } from '@/lib/aiActionEngine';
import { getMerchantPrefs, setMerchantPrefs } from '@/lib/merchantPrefs';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';

const VALID_CHARTS = new Set(['daily_visits', 'revenue_trend', 'customer_segments']);

const QUICK_REPLIES_BY_CHART = {
  daily_visits: [
    'Ask Loyalty AI: Show weekday customer trends',
    'Ask Loyalty AI: Show low-traffic hours this week',
    'Ask Loyalty AI: Which segment needs visit boost now?',
  ],
  revenue_trend: [
    'Ask Loyalty AI: How can I improve average order value?',
    'Ask Loyalty AI: Which day performed best this week?',
    'Ask Loyalty AI: Show slow revenue windows',
  ],
  customer_segments: [
    'Ask Loyalty AI: Show at-risk customers by last visit',
    'Ask Loyalty AI: Show retention trends by segment',
    'Ask Loyalty AI: Which users are near reward?',
  ],
};

const PIE_COLORS = ['var(--brand-solid)', '#818cf8', '#f59e0b', '#ef4444'];
const PENDING_AI_QUERY_KEY = 'flyback_ai_pending_query';
const INSIGHT_STYLE = {
  risk: {
    card: 'border-rose-200 bg-rose-50/70',
    badge: 'bg-rose-100 text-rose-700 border-rose-200',
    icon: AlertTriangle,
    trendClass: 'text-rose-700',
  },
  opportunity: {
    card: 'border-emerald-200 bg-emerald-50/70',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: TrendingUp,
    trendClass: 'text-emerald-700',
  },
  neutral: {
    card: 'border-amber-200 bg-amber-50/70',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: BarChart3,
    trendClass: 'text-amber-700',
  },
};
const VIP_REWARD_PROGRESS_STEPS = [
  'Finding your top customers…',
  'Understanding their recent activity…',
  'Creating special rewards for them…',
  'Getting your campaign ready…',
];
const REPEAT_VISITS_PROGRESS_STEPS = [
  'Finding customers who haven’t returned…',
  'Understanding what brings them back…',
  'Creating offers they’ll love…',
  'Getting your campaign ready…',
];
const REWARDS_PLAN_PROGRESS_STEPS = [
  'Checking how your rewards are performing…',
  'Finding ways to improve them…',
  'Updating your reward offers…',
  'Getting everything ready…',
];
const CHAT_CAMPAIGN_QUICK_ACTIONS = [
  'Run comeback campaign for inactive users',
  'Launch weekend offer',
  'Create promo for low footfall hours',
  'Reward top 10 customers',
  'Nudge customers near reward threshold',
];

const FLOW_PRESETS = {
  vip: {
    title: 'Reward High-Value Customers',
    description: 'Loyalty AI is running a live VIP campaign setup based on your latest customer data.',
    steps: VIP_REWARD_PROGRESS_STEPS,
    sentMessage: 'Campaign sent successfully.',
    finalMessage: 'Done! VIP rewards are now live for your top members ✨',
  },
  repeat_visits: {
    title: 'Improve Repeat Visits',
    description: 'Loyalty AI is preparing a repeat-visit campaign using recent customer behavior signals.',
    steps: REPEAT_VISITS_PROGRESS_STEPS,
    sentMessage: 'Campaign sent successfully.',
    finalMessage: 'Done! Repeat-visit plan is now active 🎯',
  },
  rewards_plan: {
    title: 'Improve your rewards plan',
    description: 'Loyalty AI is optimizing your rewards strategy for better repeat conversions.',
    steps: REWARDS_PLAN_PROGRESS_STEPS,
    sentMessage: 'Campaign sent successfully.',
    finalMessage: 'Done! Your rewards are now working smarter ✨',
  },
};

function toMessageId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function pause(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function sanitizeChartKey(value, fallback = 'daily_visits') {
  const key = String(value || '').trim();
  return VALID_CHARTS.has(key) ? key : fallback;
}

function cleanLine(line) {
  return String(line || '')
    .replace(/^[\s*•\-#\d.)]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitSentenceBlocks(text) {
  const pieces = String(text || '')
    .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
    .map((part) => cleanLine(part))
    .filter(Boolean);
  return pieces.length ? pieces : [cleanLine(text)];
}

function normalizeAssistantReply(raw) {
  const base = String(raw || '')
    .replace(/\r/g, '\n')
    .replace(/`/g, '')
    .trim();

  if (!base) {
    return [
      '• Insight: I need more recent activity data.',
      '• Data: Last 7-day metrics are currently unavailable.',
      '• Action: Add transactions and ask again.',
    ].join('\n');
  }

  let lines = base.split('\n').map(cleanLine).filter(Boolean);
  if (lines.length <= 1) lines = splitSentenceBlocks(base);

  const keyLine = lines.find((l) => /^(key insight|insight):/i.test(l));
  const supportLine = lines.find((l) => /^(supporting data|reason|data):/i.test(l));
  const actionLine = lines.find((l) => /^(suggested action|recommendation|action):/i.test(l));

  const fallbackKey = lines[0] || 'No clear insight identified yet.';
  const fallbackSupport = lines[1] || 'Recent trend details are limited.';
  const fallbackAction = lines[2] || 'Ask for a day-wise trend breakdown.';

  const structured = [
    keyLine ? keyLine.replace(/^key insight:/i, 'Insight:') : `Insight: ${fallbackKey}`,
    supportLine
      ? supportLine.replace(/^supporting data:/i, 'Data:')
      : `Data: ${fallbackSupport}`,
    actionLine
      ? actionLine.replace(/^suggested action:/i, 'Action:')
      : `Action: ${fallbackAction}`,
  ];

  const extras = lines
    .filter((l) => !/^(key insight|insight):/i.test(l) && !/^(supporting data|reason|data):/i.test(l) && !/^(suggested action|recommendation|action):/i.test(l))
    .slice(0, 2);

  const normalized = [...structured, ...extras].slice(0, 3).map((line) => {
    const clean = String(line || '').replace(/^(Insight|Reason|Data|Recommendation|Action):\s*/i, '').trim();
    if (/^insight:/i.test(line)) return `• Insight: ${clean}`;
    if (/^(reason|data):/i.test(line)) return `• Data: ${clean}`;
    if (/^(recommendation|action):/i.test(line)) return `• Action: ${clean}`;
    return `• ${clean}`;
  });

  return normalized.join('\n');
}

function inferChartKeyFromText(text) {
  const lower = String(text || '').toLowerCase();
  if (!lower) return 'daily_visits';
  if (lower.includes('segment') || lower.includes('retention') || lower.includes('at-risk') || lower.includes('churn')) {
    return 'customer_segments';
  }
  if (lower.includes('revenue') || lower.includes('sales') || lower.includes('average order') || lower.includes('aov')) {
    return 'revenue_trend';
  }
  if (lower.includes('visit') || lower.includes('weekday') || lower.includes('weekend') || lower.includes('lunch') || lower.includes('traffic')) {
    return 'daily_visits';
  }
  return 'daily_visits';
}

function shouldRenderChartFromQuery(query) {
  const lower = String(query || '').toLowerCase();
  if (!lower) return false;
  return (
    lower.includes('chart') ||
    lower.includes('graph') ||
    lower.includes('visual') ||
    lower.includes('plot') ||
    lower.includes('bar') ||
    lower.includes('line') ||
    lower.includes('pie') ||
    lower.includes('summary') ||
    lower.includes('overview') ||
    lower.includes('report')
  );
}

function insightTypeFromValue(value) {
  const numeric = Number(value || 0);
  if (numeric > 5) return 'opportunity';
  if (numeric < -5) return 'risk';
  return 'neutral';
}

function extractFirstCount(text) {
  const match = String(text || '').match(/(\d[\d,]*)/);
  if (!match) return 0;
  return Number(String(match[1]).replace(/,/g, '')) || 0;
}

function quickRepliesForChart(chartKey) {
  return QUICK_REPLIES_BY_CHART[sanitizeChartKey(chartKey)] || QUICK_REPLIES_BY_CHART.daily_visits;
}

function computeAudienceCounts(snapshot = {}, decisionStats = {}) {
  return {
    atRisk: Math.max(Number(decisionStats?.at_risk_30d || snapshot?.at_risk_count || 0), 0),
    lowEngagement: Math.max(Number(decisionStats?.low_engagement_1_2_visits || 0), 0),
    nearReward: Math.max(Number(decisionStats?.near_reward_100_200 || 0), 0),
    vip: Math.max(Number(decisionStats?.high_value_customers || snapshot?.retained_last_7_count || 0), 0),
  };
}

function contextAwareQuickReplies(query, reply, snapshot = {}, decisionStats = {}) {
  const lower = `${String(query || '').toLowerCase()}\n${String(reply || '').toLowerCase()}`;
  const counts = computeAudienceCounts(snapshot, decisionStats);

  const campaignCandidates = [
    counts.atRisk > 0
      ? { subtype: 'win_back', text: `Run Campaign: Win-back ${counts.atRisk} at-risk customers` }
      : null,
    counts.lowEngagement > 0
      ? { subtype: 'visit_booster', text: `Run Campaign: Boost visits for ${counts.lowEngagement} low-engagement users` }
      : null,
    counts.nearReward > 0
      ? { subtype: 'visit_booster', text: `Run Campaign: Remind ${counts.nearReward} near-reward users` }
      : null,
    counts.vip > 0
      ? { subtype: 'high_frequency_reward', text: `Run Campaign: Reward ${counts.vip} VIP users` }
      : null,
  ].filter(Boolean);

  const preferredSubtype = inferCampaignSubtypeFromText(lower);
  if (preferredSubtype) {
    campaignCandidates.sort((a, b) => {
      const aMatch = a.subtype === preferredSubtype ? 1 : 0;
      const bMatch = b.subtype === preferredSubtype ? 1 : 0;
      return bMatch - aMatch;
    });
  }

  const analysisCandidates = [
    counts.atRisk > 0 ? `Ask Loyalty AI: Show ${counts.atRisk} at-risk customers by last visit` : null,
    counts.lowEngagement > 0 ? `Ask Loyalty AI: Show ${counts.lowEngagement} low-engagement users (1-2 visits)` : null,
    counts.nearReward > 0 ? `Ask Loyalty AI: Show ${counts.nearReward} near-reward users (100-200 points away)` : null,
    !campaignCandidates.length ? 'Ask Loyalty AI: Why are no campaign audiences eligible right now?' : null,
  ].filter(Boolean);

  const isCampaignTopic =
    /\bcampaign|sms|promotion|win-?back|boost|visit|churn|inactive|at risk|near reward|vip|reward\b/.test(lower);

  const ordered = isCampaignTopic
    ? [...campaignCandidates.map((x) => x.text), ...analysisCandidates]
    : [...analysisCandidates, ...campaignCandidates.map((x) => x.text)];

  const merged = [...CHAT_CAMPAIGN_QUICK_ACTIONS, ...ordered];
  return Array.from(new Set(merged)).slice(0, 5);
}

function sanitizeQuickReplies(list, chartKey) {
  const fallback = CHAT_CAMPAIGN_QUICK_ACTIONS;
  const source = Array.isArray(list) ? list : [];
  const cleaned = source.map((item) => String(item || '').trim()).filter(Boolean);
  const withFallback = cleaned.length ? [...cleaned, ...fallback] : [...fallback, ...quickRepliesForChart(chartKey)];
  return Array.from(new Set(withFallback)).slice(0, 5);
}

function inferCampaignSubtypeFromText(text) {
  const lower = String(text || '').toLowerCase();
  if (!lower) return null;

  if (/\bbirthday|bday\b/.test(lower)) return 'birthday_campaign';
  if (/\bcustomer anniversary|dining anniversary|anniversary\b/.test(lower)) return 'customer_anniversary';
  if (/\brestaurant anniversary\b/.test(lower)) return 'restaurant_anniversary';
  if (/\bfestival|holiday|christmas|halloween|diwali|new year\b/.test(lower)) return 'festival_campaign';
  if (/\bwin-?back|churn|inactive|dormant|come back\b/.test(lower)) return 'win_back';
  if (/\bvip|top customers?|high frequency|loyal regulars?\b/.test(lower)) return 'high_frequency_reward';
  if (/\bnear-?reward|reward reminder|remind .*reward|redeem reminder|sms campaign\b/.test(lower)) return 'visit_booster';
  if (/\bvisit booster|boost visits?|traffic|weekday|weekend|geo-?based|geo\b/.test(lower)) return 'visit_booster';
  return null;
}

function mapChatQuickActionToCampaign(text, actionBySubtype = {}, snapshot = {}, decisionStats = {}) {
  const label = String(text || '').trim();
  const lower = label.toLowerCase();
  if (!CHAT_CAMPAIGN_QUICK_ACTIONS.some((item) => item.toLowerCase() === lower)) return null;

  const counts = computeAudienceCounts(snapshot, decisionStats);
  if (lower === 'run comeback campaign for inactive users') {
    const base = actionBySubtype.win_back || {};
    return {
      actionType: AI_ACTION_TYPES.RUN_CAMPAIGN,
      context: {
        ...base,
        campaign_subtype: 'win_back',
        campaign_type: base?.campaign_type || 'behavioural',
        campaign_name: base?.campaign_name || 'Come Back Offer',
        message: base?.message || 'We miss you! Come back and unlock bonus loyalty rewards this week.',
        audience_count: Math.max(Number(base?.audience_count || 0), counts.atRisk, 0),
      },
    };
  }
  if (lower === 'launch weekend offer') {
    const base = actionBySubtype.visit_booster || {};
    return {
      actionType: AI_ACTION_TYPES.RUN_CAMPAIGN,
      context: {
        ...base,
        campaign_subtype: 'visit_booster',
        campaign_type: base?.campaign_type || 'behavioural',
        campaign_name: 'Weekend Visit Booster',
        message: 'Weekend special is live. Visit and earn extra loyalty rewards.',
        audience_count: Math.max(Number(base?.audience_count || 0), counts.lowEngagement, 0),
      },
    };
  }
  if (lower === 'create promo for low footfall hours') {
    const base = actionBySubtype.visit_booster || {};
    return {
      actionType: AI_ACTION_TYPES.RUN_CAMPAIGN,
      context: {
        ...base,
        campaign_subtype: 'visit_booster',
        campaign_type: base?.campaign_type || 'behavioural',
        campaign_name: 'Low Footfall Hours Promo',
        message: 'Low-footfall hour promo is active. Visit during off-peak and earn bonus rewards.',
        audience_count: Math.max(Number(base?.audience_count || 0), counts.lowEngagement, 0),
      },
    };
  }
  if (lower === 'reward top 10 customers') {
    const base = actionBySubtype.high_frequency_reward || {};
    return {
      actionType: AI_ACTION_TYPES.RUN_CAMPAIGN,
      context: {
        ...base,
        campaign_subtype: 'high_frequency_reward',
        campaign_type: base?.campaign_type || 'behavioural',
        campaign_name: base?.campaign_name || 'VIP Thank You Offer',
        message: base?.message || 'VIP update: you are eligible for premium loyalty rewards on your next visit.',
        audience_count: 10,
      },
    };
  }
  if (lower === 'nudge customers near reward threshold') {
    const base = actionBySubtype.visit_booster || {};
    return {
      actionType: AI_ACTION_TYPES.RUN_CAMPAIGN,
      context: {
        ...base,
        campaign_subtype: 'visit_booster',
        campaign_type: base?.campaign_type || 'behavioural',
        campaign_name: 'Near Reward Reminder',
        message: 'You are very close to your next reward. Visit soon to unlock it.',
        audience_count: Math.max(Number(base?.audience_count || 0), counts.nearReward, 0),
      },
    };
  }
  return null;
}

function normalizeStoredMessage(msg, index) {
  const role = msg?.role === 'user' ? 'user' : 'assistant';
  const chartKey = msg?.chartKey ? sanitizeChartKey(msg.chartKey) : null;
  const quickReplies = role === 'assistant' ? sanitizeQuickReplies(msg?.quickReplies || [], chartKey || 'daily_visits') : [];
  return {
    id: msg?.id || `${Date.now()}-${index}`,
    role,
    content: String(msg?.content || ''),
    chartKey,
    quickReplies,
    createdAt: msg?.createdAt || new Date().toISOString(),
  };
}

function getGreetingLabel() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function ChartCard({ chartKey, charts, t }) {
  const safeKey = sanitizeChartKey(chartKey);
  const data = charts?.[safeKey] || [];
  if (!Array.isArray(data) || !data.length) return null;

  const title =
    safeKey === 'daily_visits'
      ? t('Daily Visits (Last 7 Days)')
      : safeKey === 'revenue_trend'
      ? t('Revenue Trend (Last 7 Days)')
      : t('Customer Segments');
  const Icon = safeKey === 'daily_visits' ? BarChart3 : safeKey === 'revenue_trend' ? TrendingUp : PieChartIcon;

  return (
    <div className="mt-3 rounded-xl border border-stone-200 bg-stone-50/70 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-brand" />
        <p className="text-xs font-semibold text-stone-700">{title}</p>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          {safeKey === 'daily_visits' ? (
            <BarChart data={data}>
              <CartesianGrid stroke="#eceff3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="visits" fill="var(--brand-solid)" radius={[6, 6, 0, 0]} />
            </BarChart>
          ) : safeKey === 'revenue_trend' ? (
            <LineChart data={data}>
              <CartesianGrid stroke="#eceff3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `$${Number(v || 0).toFixed(2)}`} />
              <Line type="monotone" dataKey="revenue" stroke="var(--brand-solid)" strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          ) : (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={44} outerRadius={72} paddingAngle={2}>
                {data.map((entry, idx) => (
                  <Cell key={`${entry.name}-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AiInsightsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useUiLanguage();
  const { merchantUser, merchantAiMessagesByMerchant, setMerchantAiMessages } = useStore();
  const merchantId = merchantUser?.id || 'default';
  const storedMessages = merchantAiMessagesByMerchant?.[merchantId];
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [booting, setBooting] = useState(true);
  const [charts, setCharts] = useState({});
  const [snapshot, setSnapshot] = useState({});
  const [toast, setToast] = useState('');
  const [actionBusyId, setActionBusyId] = useState('');
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => getMerchantPrefs(merchantId));
  const [draftPrefs, setDraftPrefs] = useState(() => getMerchantPrefs(merchantId));
  const [decisionInsights, setDecisionInsights] = useState({
    insight_cards: [],
    quick_actions: [],
    alerts: [],
    suggested_queries: [],
  });
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [vipCampaignFlow, setVipCampaignFlow] = useState({
    open: false,
    running: false,
    stepIndex: -1,
    audienceCount: 0,
    flowTitle: FLOW_PRESETS.vip.title,
    flowDescription: FLOW_PRESETS.vip.description,
    steps: FLOW_PRESETS.vip.steps,
    message: '',
    detailMessage: '',
    successFxKey: 0,
    error: '',
  });
  const insightsRangeDays = 7;
  const assistantName = t('Loyalty AI');
  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);
  const pendingQueryTokenRef = useRef('');

  const syncMessages = useCallback(
    (updater) => {
      setMessages((prev) => {
        const nextRaw = typeof updater === 'function' ? updater(prev) : updater;
        const next = (Array.isArray(nextRaw) ? nextRaw : []).map(normalizeStoredMessage);
        setMerchantAiMessages(merchantId, next);
        return next;
      });
    },
    [merchantId, setMerchantAiMessages]
  );

  const showToast = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3800);
  };

  const hydrateBriefing = useCallback(
    async ({ replaceConversation = false } = {}) => {
      try {
        const briefing = await apiMerchantAiBriefing(insightsRangeDays);
        const nextCharts = briefing?.charts && typeof briefing.charts === 'object' ? briefing.charts : {};
        setCharts(nextCharts);
        setSnapshot(briefing?.snapshot || {});

        if (!replaceConversation) return;

        const firstMessage = {
          id: toMessageId(),
          role: 'assistant',
          content: String(briefing?.opening_message || '').trim(),
          chartKey: null,
          quickReplies: sanitizeQuickReplies(briefing?.quick_replies || [], 'daily_visits'),
          createdAt: new Date().toISOString(),
        };
        syncMessages([firstMessage]);
      } catch (e) {
        if (replaceConversation) {
          syncMessages([
            {
              id: toMessageId(),
              role: 'assistant',
              content:
                `${getGreetingLabel()}! I analyzed your recent activity: weekend traffic is healthy, but weekday momentum can improve. ` +
                'Want a focused action plan for this week?',
              chartKey: null,
              quickReplies: quickRepliesForChart('daily_visits'),
              createdAt: new Date().toISOString(),
            },
          ]);
        }
      }
    },
    [syncMessages]
  );

  const fetchDecisionInsights = useCallback(async () => {
    setInsightsLoading(true);
    let nextData = {
      insight_cards: [],
      quick_actions: [],
      alerts: [],
      suggested_queries: [],
      stats: {},
    };
    try {
      const data = await apiMerchantDecisionInsights(insightsRangeDays);
      nextData = {
        insight_cards: Array.isArray(data?.insight_cards) ? data.insight_cards : [],
        quick_actions: Array.isArray(data?.quick_actions) ? data.quick_actions : [],
        alerts: Array.isArray(data?.alerts) ? data.alerts : [],
        suggested_queries: Array.isArray(data?.suggested_queries) ? data.suggested_queries : [],
        stats: data?.stats && typeof data.stats === 'object' ? data.stats : {},
      };
      setDecisionInsights(nextData);
    } catch {
      setDecisionInsights(nextData);
    }
    setInsightsLoading(false);
    return nextData;
  }, []);

  const runVipRewardCampaignFlow = useCallback(
    async (action) => {
      if (!action?.campaign_subtype) return;
      const flowKey = String(action?.sequence_key || '').trim();
      const preset =
        flowKey === 'repeat_visits'
          ? FLOW_PRESETS.repeat_visits
          : flowKey === 'rewards_plan'
          ? FLOW_PRESETS.rewards_plan
          : FLOW_PRESETS.vip;
      const steps = Array.isArray(preset.steps) && preset.steps.length ? preset.steps : FLOW_PRESETS.vip.steps;
      const busyId = `action-${action?.campaign_subtype || 'default'}`;
      setActionBusyId(busyId);
      setVipCampaignFlow({
        open: true,
        running: true,
        stepIndex: 0,
        audienceCount: Math.max(Number(action.audience_count || 0), 0),
        flowTitle: preset.title,
        flowDescription: preset.description,
        steps,
        message: '',
        detailMessage: '',
        successFxKey: 0,
        error: '',
      });

      try {
        const latest = await fetchDecisionInsights();
        const liveAudience = Math.max(
          Number(latest?.stats?.high_value_customers || action?.audience_count || 0),
          0
        );

        setVipCampaignFlow((prev) => ({
          ...prev,
          stepIndex: 0,
          audienceCount: liveAudience,
        }));
        await pause(900);

        setVipCampaignFlow((prev) => ({ ...prev, stepIndex: 1 }));
        await pause(900);

        setVipCampaignFlow((prev) => ({ ...prev, stepIndex: 2 }));
        const executionPromise = executeAIAction(AI_ACTION_TYPES.RUN_CAMPAIGN, {
          campaign_type: action.campaign_type,
          campaign_subtype: action.campaign_subtype,
          campaign_name: action.campaign_name,
          message: action.message,
          audience_count: liveAudience,
          send_now: action.send_now !== false,
          scheduled_for: action.scheduled_for || '',
        });
        await pause(900);

        setVipCampaignFlow((prev) => ({ ...prev, stepIndex: 3 }));
        const res = await executionPromise;
        await pause(600);
        setVipCampaignFlow((prev) => ({
          ...prev,
          running: false,
          message: t(preset.sentMessage),
          detailMessage: '',
          successFxKey: Date.now(),
          error: '',
        }));
        await pause(1700);
        setVipCampaignFlow((prev) => ({
          ...prev,
          running: false,
          message: t(preset.finalMessage),
          detailMessage: String(res?.message || ''),
          successFxKey: Date.now(),
          error: '',
        }));
        window.setTimeout(() => {
          setVipCampaignFlow((prev) => ({ ...prev, open: false }));
        }, 3700);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const errorText = typeof detail === 'string' ? detail : t('Action failed. Tap to retry.');
        setVipCampaignFlow((prev) => ({
          ...prev,
          running: false,
          detailMessage: '',
          successFxKey: 0,
          error: errorText,
        }));
        showToast(`❌ ${errorText}`);
      }
      setActionBusyId('');
    },
    [fetchDecisionInsights, t]
  );

  const openCampaignFromAction = useCallback(
    async (action) => {
      if (!action?.campaign_subtype) return;
      if (
        action?.campaign_subtype === 'high_frequency_reward' ||
        action?.sequence_key === 'repeat_visits' ||
        action?.sequence_key === 'rewards_plan'
      ) {
        await runVipRewardCampaignFlow(action);
        return;
      }
      const busyId = `action-${action?.campaign_subtype || 'default'}`;
      setActionBusyId(busyId);
      try {
        const res = await executeAIAction(AI_ACTION_TYPES.RUN_CAMPAIGN, {
          campaign_type: action.campaign_type,
          campaign_subtype: action.campaign_subtype,
          campaign_name: action.campaign_name,
          message: action.message,
          audience_count: Number(action.audience_count || 0),
          send_now: action.send_now !== false,
          scheduled_for: action.scheduled_for || '',
        });
        showToast(`✅ ${res.message}`);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        showToast(`❌ ${typeof detail === 'string' ? detail : t('Action failed. Tap to retry.')}`);
      }
      setActionBusyId('');
    },
    [runVipRewardCampaignFlow, t]
  );

  useEffect(() => {
    let cancelled = false;
    async function init() {
      setBooting(true);
      setPrefs(getMerchantPrefs(merchantId));
      setDraftPrefs(getMerchantPrefs(merchantId));

      const normalizedStored = Array.isArray(storedMessages) ? storedMessages.map(normalizeStoredMessage) : [];
      const legacyGreeting = normalizedStored.some((m) =>
        m.role === 'assistant' && m.content.toLowerCase().includes("hi! i'm your ai loyalty assistant")
      );

      if (normalizedStored.length && !legacyGreeting) {
        setMessages(normalizedStored);
        await hydrateBriefing({ replaceConversation: false });
      } else {
        await hydrateBriefing({ replaceConversation: true });
      }
      await fetchDecisionInsights();

      if (!cancelled) setBooting(false);
    }

    init();
    return () => {
      cancelled = true;
    };
    // intentionally keyed by merchant context only; chat updates should not re-run init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDecisionInsights, hydrateBriefing, merchantId]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const latestAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') return i;
    }
    return -1;
  }, [messages]);
  const decisionStats = useMemo(() => decisionInsights?.stats || {}, [decisionInsights?.stats]);

  const submitMessage = useCallback(
    async (text) => {
      const content = String(text || '').trim();
      if (!content || sending) return;

      const userMessage = {
        id: toMessageId(),
        role: 'user',
        content,
        chartKey: null,
        quickReplies: [],
        createdAt: new Date().toISOString(),
      };

      const requestMessages = [...messages, userMessage].map((m) => ({ role: m.role, content: m.content }));
      syncMessages((prev) => [...prev, userMessage]);
      setInput('');
      setSending(true);

      try {
        const res = await apiSendChat(requestMessages, { ...(prefs || {}), time_range_days: insightsRangeDays });
        const normalized = normalizeAssistantReply(res?.reply || '');
        const wantsChart = shouldRenderChartFromQuery(content);
        const inferredChart = wantsChart
          ? sanitizeChartKey(inferChartKeyFromText(`${content}\n${normalized}`))
          : null;
        const latestInsights = await fetchDecisionInsights();
        const latestStats =
          latestInsights?.stats && typeof latestInsights.stats === 'object'
            ? latestInsights.stats
            : decisionStats;
        const dynamicQuickReplies = contextAwareQuickReplies(content, normalized, snapshot, latestStats);

        const assistantMessage = {
          id: toMessageId(),
          role: 'assistant',
          content: normalized,
          chartKey: inferredChart,
          quickReplies: sanitizeQuickReplies(dynamicQuickReplies, inferredChart || inferChartKeyFromText(content)),
          createdAt: new Date().toISOString(),
        };
        syncMessages((prev) => [...prev, assistantMessage]);
      } catch (e) {
        syncMessages((prev) => [
          ...prev,
          {
            id: toMessageId(),
            role: 'assistant',
            content:
              'Insight: I could not complete this analysis right now.\n' +
              'Reason: Connection to the AI service was interrupted.\n' +
              'Recommendation: Please retry in a few seconds.',
            chartKey: null,
            quickReplies: [],
            createdAt: new Date().toISOString(),
          },
        ]);
      } finally {
        setSending(false);
        inputRef.current?.focus();
      }
    },
    [messages, sending, syncMessages, prefs, snapshot, decisionStats, fetchDecisionInsights]
  );

  useEffect(() => {
    if (booting || sending) return;

    const stateQuery = String(location?.state?.pendingQuery || '').trim();
    const stateToken = String(location?.state?.pendingQueryToken || '').trim();
    let storedQuery = '';
    let storedToken = '';

    if (typeof window !== 'undefined') {
      const raw = window.sessionStorage.getItem(PENDING_AI_QUERY_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            storedQuery = String(parsed.query || '').trim();
            storedToken = String(parsed.token || '').trim();
          }
        } catch {
          storedQuery = String(raw || '').trim();
        }
      }
    }

    const query = stateQuery || storedQuery;
    const token = stateToken || storedToken || `${query}-fallback`;
    if (!query) return;
    if (pendingQueryTokenRef.current && pendingQueryTokenRef.current === token) return;

    pendingQueryTokenRef.current = token;
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(PENDING_AI_QUERY_KEY);
    }
    submitMessage(query);

    if (stateQuery) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [booting, location.pathname, location?.state, navigate, sending, submitMessage]);

  const openCustomize = () => {
    setDraftPrefs(prefs || {});
    setCustomizeOpen(true);
  };

  const saveCustomize = () => {
    const payload = {
      primary_goal: String(draftPrefs?.primary_goal || '').trim(),
      peak_hours: String(draftPrefs?.peak_hours || '').trim(),
      target_customer: String(draftPrefs?.target_customer || '').trim(),
      manual_context: String(draftPrefs?.manual_context || '').trim(),
      ai_personality: String(draftPrefs?.ai_personality || '').trim(),
    };
    const next = setMerchantPrefs(merchantId, payload);
    setPrefs(next);
    setCustomizeOpen(false);
    showToast(t('AI preferences saved'));
  };

  const refreshBriefing = async () => {
    setBooting(true);
    await hydrateBriefing({ replaceConversation: true });
    await fetchDecisionInsights();
    setBooting(false);
    showToast(t('Insights refreshed from latest 7-day data'));
  };

  const snapshotSummary = useMemo(() => {
    const visits = Number(snapshot?.visits_last_7 || 0);
    const growth = Number(snapshot?.visits_growth_last_7_vs_prev || 0);
    const atRisk = Number(snapshot?.at_risk_count || 0);
    const revenue = Number(snapshot?.revenue_last_7 || 0);
    return [
      { label: t('Visits (7d)'), value: visits.toLocaleString() },
      { label: t('Visit Growth'), value: `${growth >= 0 ? '+' : ''}${growth}%` },
      { label: t('At-Risk Guests'), value: atRisk.toLocaleString() },
      { label: t('Revenue (7d)'), value: `$${revenue.toFixed(0)}` },
    ];
  }, [snapshot, t]);

  const insightCards = useMemo(() => decisionInsights?.insight_cards || [], [decisionInsights?.insight_cards]);
  const quickCampaignActions = useMemo(() => decisionInsights?.quick_actions || [], [decisionInsights?.quick_actions]);
  const contextualAlerts = useMemo(() => decisionInsights?.alerts || [], [decisionInsights?.alerts]);
  const actionBySubtype = useMemo(() => {
    const map = {};
    [...quickCampaignActions, ...insightCards.map((card) => card?.action).filter(Boolean)].forEach((action) => {
      if (action?.campaign_subtype) map[action.campaign_subtype] = action;
    });
    return map;
  }, [quickCampaignActions, insightCards]);
  const handleReplyQuickAction = useCallback(
    async (text) => {
      const chatCampaignAction = mapChatQuickActionToCampaign(text, actionBySubtype, snapshot, decisionStats);
      if (chatCampaignAction) {
        const busyId = `chat-quick-${Date.now()}`;
        setActionBusyId(busyId);
        try {
          const res = await executeAIAction(chatCampaignAction.actionType, chatCampaignAction.context);
          syncMessages((prev) => [
            ...prev,
            {
              id: toMessageId(),
              role: 'assistant',
              content: [
                `• Action executed: ${text}`,
                `• Result: ${String(res?.message || 'Campaign executed successfully.')}`,
                '• Next: Track delivery and conversion in Campaign Activity.',
              ].join('\n'),
              chartKey: null,
              quickReplies: sanitizeQuickReplies(CHAT_CAMPAIGN_QUICK_ACTIONS, 'daily_visits'),
              createdAt: new Date().toISOString(),
            },
          ]);
        } catch (e) {
          const detail = e?.response?.data?.detail;
          const err = typeof detail === 'string' ? detail : 'Action could not be completed right now.';
          syncMessages((prev) => [
            ...prev,
            {
              id: toMessageId(),
              role: 'assistant',
              content: [
                `• Action not completed: ${text}`,
                `• Reason: ${err}`,
                '• Next: Try again in a few seconds.',
              ].join('\n'),
              chartKey: null,
              quickReplies: sanitizeQuickReplies(CHAT_CAMPAIGN_QUICK_ACTIONS, 'daily_visits'),
              createdAt: new Date().toISOString(),
            },
          ]);
        }
        setActionBusyId('');
        return;
      }

      const subtype = inferCampaignSubtypeFromText(text);
      if (!subtype) {
        submitMessage(text);
        return;
      }

      const fallbackType = subtype.includes('campaign') ? 'occasion_based' : 'behavioural';
      const fallbackLabelMap = {
        birthday_campaign: 'Birthday Campaign',
        customer_anniversary: 'Customer Anniversary',
        restaurant_anniversary: 'Restaurant Anniversary',
        festival_campaign: 'Festival Campaign',
        win_back: 'Win-back Campaign',
        visit_booster: 'Visit Booster',
        high_frequency_reward: 'VIP Reward',
      };
      const action = actionBySubtype[subtype] || {
        campaign_subtype: subtype,
        campaign_type: fallbackType,
        label: t(fallbackLabelMap[subtype] || 'Run Campaign'),
      };
      openCampaignFromAction(action);
    },
    [actionBySubtype, decisionStats, openCampaignFromAction, snapshot, submitMessage, syncMessages, t]
  );

  const priorityInsights = useMemo(() => {
    const fromBackend = (Array.isArray(insightCards) ? insightCards : [])
      .filter((card) => card && String(card.title || '').trim())
      .slice(0, 3)
      .map((card, idx) => {
        const cardId = String(card.id || '').trim();
        const title = String(card.title || '');
        const backendAction = card.action || {};
        const audienceCount = Math.max(
          Number(card.audience_count || backendAction.audience_count || extractFirstCount(title) || 0),
          0
        );
        let mappedAction = backendAction
          ? {
              ...backendAction,
              audience_count: audienceCount,
            }
          : null;
        if (mappedAction && cardId === 'visit_drop') {
          mappedAction = {
            ...mappedAction,
            label: t('Action'),
            sequence_key: 'repeat_visits',
            campaign_name: mappedAction?.campaign_name || 'Improve Repeat Visits',
          };
        }
        if (mappedAction && cardId === 'near_reward') {
          mappedAction = {
            ...mappedAction,
            label: t('Action'),
            sequence_key: 'rewards_plan',
            campaign_name: mappedAction?.campaign_name || 'Improve Rewards Plan',
          };
        }
        return {
          id: cardId || `priority-${idx}`,
          type: ['risk', 'opportunity', 'neutral'].includes(card.type) ? card.type : insightTypeFromValue(card?.value),
          title:
            cardId === 'near_reward'
              ? t('Improve your rewards plan')
              : cardId === 'visit_drop'
              ? t('Improve Repeat Visits')
              : title,
          impact: String(card.recommendation || card.impact || ''),
          trend: String(card.trend || ''),
          audienceCount,
          action: mappedAction,
        };
      });
    if (fromBackend.length) return fromBackend;

    const inactiveCount = Number(decisionStats?.at_risk_30d || snapshot?.at_risk_count || 0);
    const visitDropPct = Number(decisionStats?.visit_drop_pct || 0);
    const lowEngagement = Number(decisionStats?.low_engagement_1_2_visits || 0);
    const nearRewardCount = Number(decisionStats?.near_reward_100_200 || 0);

    return [
      {
        id: 'priority-inactive',
        type: 'risk',
        title: `${inactiveCount} ${t('customers inactive (30+ days)')}`,
        impact: inactiveCount > 0 ? t('Revenue at risk from churned members.') : t('No major churn signal right now.'),
        trend: inactiveCount > 0 ? `${t('High risk audience')}: ${inactiveCount}` : t('Stable'),
        audienceCount: inactiveCount,
        action: actionBySubtype.win_back || {
          label: t('Run Win-back Campaign'),
          campaign_subtype: 'win_back',
          campaign_type: 'behavioural',
          audience_count: inactiveCount,
        },
      },
      {
        id: 'priority-visits',
        type: insightTypeFromValue(-visitDropPct),
        title: t('Improve Repeat Visits'),
        impact: visitDropPct > 0 ? t('Traffic slowdown detected vs previous week.') : t('No week-over-week visit decline.'),
        trend: `${snapshot?.visits_last_7 || 0} ${t('vs')} ${snapshot?.visits_prev_7 || 0} ${t('visits')}`,
        audienceCount: lowEngagement,
        action: actionBySubtype.visit_booster || {
          label: t('Action'),
          campaign_subtype: 'visit_booster',
          campaign_type: 'behavioural',
          audience_count: lowEngagement,
          sequence_key: 'repeat_visits',
          campaign_name: 'Improve Repeat Visits',
        },
      },
      {
        id: 'priority-reward',
        type: 'opportunity',
        title: t('Improve your rewards plan'),
        impact: nearRewardCount > 0 ? t('High conversion opportunity with a reminder.') : t('Fewer users near reward threshold today.'),
        trend: t('Within 100-200 points of next reward'),
        audienceCount: nearRewardCount,
        action: actionBySubtype.visit_booster || {
          label: t('Action'),
          campaign_subtype: 'visit_booster',
          campaign_type: 'behavioural',
          audience_count: nearRewardCount,
          sequence_key: 'rewards_plan',
          campaign_name: 'Improve Rewards Plan',
        },
      },
    ];
  }, [actionBySubtype, decisionStats?.at_risk_30d, decisionStats?.low_engagement_1_2_visits, decisionStats?.near_reward_100_200, decisionStats?.visit_drop_pct, insightCards, snapshot?.at_risk_count, snapshot?.visits_last_7, snapshot?.visits_prev_7, t]);

  const activeContextAlert = useMemo(() => null, []);
  const suggestedQueries = useMemo(() => {
    const source = decisionInsights?.suggested_queries || [];
    const banned = ['why are visits dropping', 'how can i increase repeat customers', 'which users should i target'];
    const clean = source
      .map((q) => String(q || '').trim())
      .filter(Boolean)
      .filter((q) => !banned.includes(q.toLowerCase()))
      .slice(0, 4);
    return clean.length ? clean : [
      // t('Show at-risk customer trend'),
      // t('Best campaign to run today'),
      // t('How to improve visit frequency'),
    ];
  }, [decisionInsights?.suggested_queries]);

  return (
    <div className="space-y-4 w-full" data-testid="ai-insights-page">
      <CenteredStatusPopup message={toast} />

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-stone-200 bg-white p-4"
      >
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl gradient-brand text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                {assistantName}
              </h1>
              <p className="text-sm text-stone-500">{t('Context-aware restaurant advisor powered by your real business data.')}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openCustomize}
              className="h-10 px-4 rounded-xl border border-stone-200 bg-white text-sm font-semibold flex items-center gap-2 hover:bg-stone-50"
            >
              <SlidersHorizontal className="w-4 h-4" /> {t('Customize AI')}
            </button>
            <button
              type="button"
              onClick={refreshBriefing}
              className="h-10 px-4 rounded-xl border border-stone-200 bg-white text-sm font-semibold flex items-center gap-2 hover:bg-stone-50"
            >
              <RefreshCw className="w-4 h-4" /> {t('Refresh Insights')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          {snapshotSummary.map((item) => (
            <div key={item.label} className="rounded-xl border border-stone-200 bg-stone-50/70 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{t(item.label)}</p>
              <p className="text-lg font-bold text-[#0f172a]">{item.value}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        {insightsLoading
          ? Array.from({ length: 3 }).map((_, idx) => (
              <div key={`insight-skeleton-${idx}`} className="rounded-2xl border border-stone-200 bg-white p-4">
                <div className="h-4 w-28 rounded bg-stone-100 animate-pulse mb-3" />
                <div className="h-7 w-12 rounded bg-stone-100 animate-pulse mb-2" />
                <div className="h-3 w-full rounded bg-stone-100 animate-pulse mb-1.5" />
                <div className="h-3 w-4/5 rounded bg-stone-100 animate-pulse" />
              </div>
            ))
          : null}

        {!insightsLoading && !priorityInsights.length ? (
          <div className="xl:col-span-3 rounded-2xl border border-stone-200 bg-white p-6 text-center">
            <p className="text-sm font-semibold text-stone-700">{t('No insights available yet')}</p>
            <p className="text-xs text-stone-500 mt-1">{t('Once more transaction data is available, AI will surface risks and opportunities here.')}</p>
          </div>
        ) : null}

        {!insightsLoading &&
          priorityInsights.slice(0, 3).map((card, idx) => {
            const typeKey = ['risk', 'opportunity', 'neutral'].includes(card?.type) ? card.type : 'neutral';
            const style = INSIGHT_STYLE[typeKey];
            const Icon = style.icon;
            const isFlatVisit =
              String(card?.id || '').trim() === 'visit_drop' &&
              Math.abs(Number(card?.value || 0)) <= 0.1;
            const badgeText = isFlatVisit ? t('Stable') : t(typeKey);
            const trendText = String(card?.trend || '');
            const showDown = trendText.toLowerCase().includes('drop') || trendText.includes('-');
            const TrendIcon = showDown ? TrendingDown : TrendingUp;
            const actionLabel = t(String(card?.action?.label || t('Run Campaign')).trim() || t('Run Campaign'));
            return (
              <motion.div
                key={card?.id || `insight-${idx}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl border p-4 ${style.card}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <Badge className={`rounded-full border text-xs ${style.badge}`}>{badgeText}</Badge>
                  <Icon className={`w-4 h-4 ${style.trendClass}`} />
                </div>
                <p className="mt-2 text-sm font-semibold text-stone-800">{card?.title}</p>
                {/* <p className={`mt-1 text-xs font-semibold inline-flex items-center gap-1 ${style.trendClass}`}>
                  <TrendIcon className="w-3.5 h-3.5" /> {trendText || t('Trend data unavailable')}
                </p> */}
                {/* <p className="mt-2 text-xs text-stone-600">{card?.impact || card?.recommendation || t('No recommendation available.')}</p> */}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => openCampaignFromAction(card?.action)}
                    disabled={actionBusyId === `action-${card?.action?.campaign_subtype || 'default'}`}
                    className="h-8 px-3 rounded-lg border border-brand/25 bg-white text-brand text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    {actionBusyId === `action-${card?.action?.campaign_subtype || 'default'}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {actionLabel} <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <div className="px-4 pt-4 pb-2 border-b border-stone-100 bg-stone-50/50">
          <p className="text-sm font-semibold text-stone-800">{t('AI Chat')}</p>
          <p className="text-xs text-stone-500 mt-0.5">{t('Ask follow-up questions and drill deeper into the signals above.')}</p>
        </div>
        <div ref={chatScrollRef} className="h-[62vh] overflow-y-auto p-4 space-y-4">
          {booting ? (
            <div className="h-full min-h-[260px] flex items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-stone-500">
                <Loader2 className="w-4 h-4 animate-spin" /> {t('Building your last 7-day business briefing...')}
              </div>
            </div>
          ) : null}

          {!booting &&
            messages.map((msg, index) => {
              const isUser = msg.role === 'user';
              const isLatestAssistant = !isUser && index === latestAssistantIndex;

              return (
                <motion.div
                  key={msg.id || `${msg.createdAt}-${index}`}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`${isUser ? 'max-w-[76%] chat-bubble-user px-4 py-3' : 'w-full max-w-[88%] rounded-2xl border border-stone-200 bg-stone-50/50 px-4 py-3'}`}>
                    <p className={`text-sm whitespace-pre-line ${isUser ? 'text-white' : 'text-stone-800'}`}>{msg.content}</p>

                    {!isUser && msg.chartKey ? <ChartCard chartKey={msg.chartKey} charts={charts} t={t} /> : null}

                    {!isUser && isLatestAssistant && msg.quickReplies?.length ? (
                      <div className="mt-3 pt-3 border-t border-stone-200">
                        <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">{t('Quick actions')}</p>
                        <div className="flex flex-wrap gap-2">
                          {msg.quickReplies.map((reply) => (
                            <button
                              key={`${msg.id}-${reply}`}
                              type="button"
                              onClick={() => handleReplyQuickAction(reply)}
                              className="h-8 px-3 rounded-lg border border-brand/25 bg-brand-soft text-brand text-xs font-semibold hover:opacity-90"
                            >
                              {reply}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              );
            })}

          {sending ? (
            <div className="flex justify-start">
              <div className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 text-stone-600 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t('Analyzing your data...')}
              </div>
            </div>
          ) : null}
        </div>

        <div className="border-t border-stone-200 p-3 bg-white">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {suggestedQueries.map((query) => (
              <button
                key={query}
                type="button"
                onClick={() => submitMessage(query)}
                className="h-7 px-2 rounded-md border border-stone-200 bg-stone-50 text-[11px] font-semibold text-stone-600 hover:bg-stone-100"
              >
                {query}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitMessage(input);
                }
              }}
              placeholder={t('Ask for trends, retention risks, revenue signals, or campaign ideas...')}
              className="flex-1 h-11 px-4 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
              data-testid="ai-chat-input"
            />
            <button
              type="button"
              className="h-11 w-11 rounded-xl border border-stone-200 bg-white text-stone-600 flex items-center justify-center"
              title={t('Voice input coming soon')}
              aria-label={t('Voice input coming soon')}
            >
              <Mic className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => submitMessage(input)}
              disabled={!input.trim() || sending || booting}
              className="h-11 px-4 rounded-xl gradient-brand-btn text-sm font-semibold disabled:opacity-55 flex items-center gap-2"
              data-testid="ai-chat-send-btn"
            >
              <Send className="w-4 h-4" /> {t('Send')}
            </button>
          </div>
        </div>
      </div>

      <Dialog
        open={vipCampaignFlow.open}
        onOpenChange={(nextOpen) => {
          if (vipCampaignFlow.running) return;
          setVipCampaignFlow((prev) => ({ ...prev, open: nextOpen }));
        }}
      >
        <DialogContent className="max-w-xl rounded-2xl">
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {t(vipCampaignFlow.flowTitle || FLOW_PRESETS.vip.title)}
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            {t(vipCampaignFlow.flowDescription || FLOW_PRESETS.vip.description)}
          </DialogDescription>

          <div className="relative rounded-xl border border-brand/20 bg-gradient-to-br from-brand-soft via-white to-brand-soft/40 p-3 overflow-hidden">
            {vipCampaignFlow.running ? (
              <div className="flex items-center gap-3">
                <div className="relative h-12 w-12 shrink-0">
                  <motion.span
                    className="absolute inset-0 rounded-full border border-brand/35"
                    animate={{ scale: [1, 1.35], opacity: [0.7, 0] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
                  />
                  <motion.span
                    className="absolute inset-1 rounded-full border border-brand/20"
                    animate={{ scale: [1, 1.25], opacity: [0.65, 0] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut', delay: 0.25 }}
                  />
                  <motion.div
                    className="relative h-12 w-12 rounded-full bg-white border border-brand/20 flex items-center justify-center shadow-sm"
                    animate={{ y: [0, -5, 0], rotate: [-8, 8, -8], scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.15, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Rocket className="w-5 h-5 text-brand" />
                  </motion.div>
                  <motion.span
                    className="absolute left-1/2 -translate-x-1/2 -bottom-1 h-2.5 w-2 rounded-full bg-gradient-to-b from-amber-300 to-orange-500"
                    animate={{ scaleY: [0.85, 1.35, 0.85], opacity: [0.55, 1, 0.55] }}
                    transition={{ duration: 0.55, repeat: Infinity, ease: 'easeInOut' }}
                  />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-brand">{t('Processing')}</p>
                  <p className="text-sm font-semibold text-[#0f172a]">
                    {t((vipCampaignFlow.steps || FLOW_PRESETS.vip.steps)[Math.max(vipCampaignFlow.stepIndex, 0)] || '')}
                  </p>
                </div>
              </div>
            ) : vipCampaignFlow.message ? (
              <div key={vipCampaignFlow.successFxKey || 'vip-success'} className="relative flex items-center gap-3">
                <motion.div
                  className="h-10 w-10 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center"
                  initial={{ scale: 0.8, opacity: 0.7 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                </motion.div>
                <div>
                  <p className="text-sm font-bold text-emerald-800">{vipCampaignFlow.message}</p>
                  {vipCampaignFlow.detailMessage ? (
                    <p className="text-xs text-stone-600 mt-0.5">{vipCampaignFlow.detailMessage}</p>
                  ) : null}
                </div>

                <motion.div
                  className="absolute -top-2 left-7 text-amber-400"
                  initial={{ opacity: 0, scale: 0.2, y: 6 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.2, 1.35, 0.85], y: [6, -16, -30], x: [0, -4, -7] }}
                  transition={{ duration: 1.25, repeat: 2, ease: 'easeOut' }}
                >
                  <Star className="w-4 h-4 fill-current" />
                </motion.div>
                <motion.div
                  className="absolute -top-1 right-8 text-yellow-400"
                  initial={{ opacity: 0, scale: 0.3, y: 2 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.3, 1.3, 0.7], y: [2, -14, -28], x: [0, 3, 8] }}
                  transition={{ duration: 1.2, repeat: 2, ease: 'easeOut', delay: 0.16 }}
                >
                  <Star className="w-3.5 h-3.5 fill-current" />
                </motion.div>
                <motion.div
                  className="absolute -bottom-1 right-1 text-orange-300"
                  initial={{ opacity: 0, scale: 0.4, y: 2 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.4, 1.25, 0.8], y: [2, -10, -20], x: [0, 2, 6] }}
                  transition={{ duration: 1.15, repeat: 2, ease: 'easeOut', delay: 0.3 }}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                </motion.div>
                <motion.div
                  className="absolute -bottom-2 left-9 text-emerald-300"
                  initial={{ opacity: 0, scale: 0.4, y: 0 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.4, 1.2, 0.85], y: [0, -12, -24], x: [0, -2, -6] }}
                  transition={{ duration: 1.25, repeat: 2, ease: 'easeOut', delay: 0.22 }}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                </motion.div>
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
            <p className="text-sm font-semibold text-emerald-800">
              {t('Top customers detected')}: {Math.max(Number(vipCampaignFlow.audienceCount || 0), 0)}
            </p>
          </div>

          <div className="space-y-2.5">
            {(vipCampaignFlow.steps || FLOW_PRESETS.vip.steps).map((step, idx) => {
              const done = !vipCampaignFlow.running && vipCampaignFlow.error ? false : idx < vipCampaignFlow.stepIndex;
              const active = vipCampaignFlow.running && idx === vipCampaignFlow.stepIndex;
              const pending = !done && !active;
              return (
                <div
                  key={step}
                  className={`h-11 rounded-xl border px-3 flex items-center gap-2.5 ${
                    done
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : active
                      ? 'border-brand/35 bg-brand-soft text-brand'
                      : 'border-stone-200 bg-stone-50 text-stone-500'
                  }`}
                >
                  {done ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : active ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-current opacity-60" />
                  )}
                  <span className={`text-sm ${pending ? 'font-medium' : 'font-semibold'}`}>{t(step)}</span>
                </div>
              );
            })}
          </div>

          {vipCampaignFlow.message ? null : null}
          {vipCampaignFlow.error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700">
              ❌ {vipCampaignFlow.error}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {t('Customize AI')}
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            {t('Train your Flyback Copilot with business goals so suggestions stay focused and actionable.')}
          </DialogDescription>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-stone-600">{t('Primary goal')}</p>
              <select
                value={draftPrefs?.primary_goal || ''}
                onChange={(e) => setDraftPrefs((prev) => ({ ...prev, primary_goal: e.target.value }))}
                className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm"
              >
                <option value="">{t('Select goal')}</option>
                <option value="Increase visits">{t('Increase visits')}</option>
                <option value="Increase revenue">{t('Increase revenue')}</option>
                <option value="Improve retention">{t('Improve retention')}</option>
                <option value="Boost average order value">{t('Boost average order value')}</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-stone-600">{t('Peak business hours')}</p>
              <Input
                value={draftPrefs?.peak_hours || ''}
                onChange={(e) => setDraftPrefs((prev) => ({ ...prev, peak_hours: e.target.value }))}
                placeholder={t('e.g., Fri-Sat 7 PM - 10 PM')}
                className="h-10 rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-stone-600">{t('Target customer type')}</p>
              <Input
                value={draftPrefs?.target_customer || ''}
                onChange={(e) => setDraftPrefs((prev) => ({ ...prev, target_customer: e.target.value }))}
                placeholder={t('e.g., Families, office lunch crowd')}
                className="h-10 rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-stone-600">{t('AI personality')}</p>
              <select
                value={draftPrefs?.ai_personality || ''}
                onChange={(e) => setDraftPrefs((prev) => ({ ...prev, ai_personality: e.target.value }))}
                className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm"
              >
                <option value="">{t('Balanced')}</option>
                <option value="Growth-focused strategist">{t('Growth-focused strategist')}</option>
                <option value="Retention specialist">{t('Retention specialist')}</option>
                <option value="Operational optimizer">{t('Operational optimizer')}</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-stone-600">{t('Manual business context')}</p>
            <Textarea
              value={draftPrefs?.manual_context || ''}
              onChange={(e) => setDraftPrefs((prev) => ({ ...prev, manual_context: e.target.value }))}
              placeholder={t('Add any special context, offers, staffing constraints, or campaign preferences...')}
              className="min-h-[92px] rounded-xl"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {prefs?.primary_goal ? <Badge className="bg-brand-soft text-brand border border-brand/20">{prefs.primary_goal}</Badge> : null}
            {prefs?.peak_hours ? <Badge className="bg-stone-100 text-stone-700 border border-stone-200">Peak: {prefs.peak_hours}</Badge> : null}
            {prefs?.target_customer ? <Badge className="bg-stone-100 text-stone-700 border border-stone-200">Target: {prefs.target_customer}</Badge> : null}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setCustomizeOpen(false)}
              className="h-10 rounded-xl border border-stone-200 text-sm font-semibold hover:bg-stone-50"
            >
              {t('Cancel')}
            </button>
            <button type="button" onClick={saveCustomize} className="h-10 rounded-xl gradient-brand-btn text-sm font-semibold">
              {t('Save Preferences')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
